import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { entrepriseService } from '../services/entreprise.service';
import './BonsCommande.css';

interface LigneCommande {
  id?: number;
  produitId: number;
  produitNom: string;
  produitCode?: string;
  prixUnitaire: number;
  quantite: number;
  quantiteRecue?: number;
  sousTotal: number;
}

interface Commande {
  id: number;
  numero: string;
  fournisseurId: number;
  fournisseurNom: string;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  tvaActive: boolean;
  statut: 'brouillon' | 'envoyee' | 'partielle' | 'recue' | 'annulee';
  dateLivraisonPrevue: string;
  dateReception: string;
  notes: string;
  lignes: LigneCommande[];
  createdAt: string;
}

interface Fournisseur {
  id: number;
  nom: string;
  telephone: string;
  email: string;
  adresse: string;
  ville: string;
}

interface Produit {
  id: number;
  nom: string;
  reference: string;
  codeBarres: string;
  prixAchat: number;
  stockActuel: number;
}

interface Stats {
  total: number;
  enCours: number;
  recues: number;
  montantEnCours: number;
}

const fmt = (n: number) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const STATUTS: Record<string, { label: string; classe: string }> = {
  brouillon: { label: 'Brouillon', classe: 'brouillon' },
  envoyee: { label: 'Envoyée', classe: 'envoyee' },
  partielle: { label: 'Partielle', classe: 'partielle' },
  recue: { label: 'Reçue', classe: 'recue' },
  annulee: { label: 'Annulée', classe: 'annulee' },
};

export default function BonsCommande() {
  // En-tête réel de l'entreprise (plus de valeurs de démo en dur).
  const [configEntreprise, setConfigEntreprise] = useState({
    nom: 'NexaCom', adresse: '', telephone: '', email: '', ifu: '',
  });
  useEffect(() => {
    entrepriseService.getInfos().then((infos) => {
      setConfigEntreprise({
        nom: infos.nom || 'NexaCom',
        adresse: entrepriseService.adresseComplete(infos),
        telephone: infos.telephone,
        email: infos.email,
        ifu: infos.ifu,
      });
    });
  }, []);
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [produits, setProduits] = useState<Produit[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [commandeSel, setCommandeSel] = useState<Commande | null>(null);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  // Formulaire de création
  const [fournisseurId, setFournisseurId] = useState<number | ''>('');
  const [tvaActive, setTvaActive] = useState(false);
  const [dateLivraison, setDateLivraison] = useState('');
  const [notes, setNotes] = useState('');
  const [lignes, setLignes] = useState<LigneCommande[]>([]);
  const [produitAjout, setProduitAjout] = useState<number | ''>('');

  useEffect(() => {
    chargerTout();
  }, []);

  const chargerTout = async () => {
    try {
      const [cmdRes, fourRes, prodRes, statsRes] = await Promise.all([
        api.get('/commandes'),
        api.get('/fournisseurs'),
        api.get('/produits'),
        api.get('/commandes/stats'),
      ]);
      setCommandes(cmdRes.data);
      setFournisseurs(fourRes.data);
      setProduits(prodRes.data);
      setStats(statsRes.data);
    } catch (e) {
      setErreur('Impossible de charger les données');
    } finally {
      setChargement(false);
    }
  };

  const notifier = (msg: string) => {
    setSucces(msg);
    setTimeout(() => setSucces(''), 3000);
  };

  const ouvrirCreation = () => {
    setFournisseurId('');
    setTvaActive(false);
    setDateLivraison('');
    setNotes('');
    setLignes([]);
    setProduitAjout('');
    setErreur('');
    setShowModal(true);
  };

  const ajouterLigne = () => {
    if (!produitAjout) return;
    const p = produits.find(x => x.id === produitAjout);
    if (!p) return;
    if (lignes.some(l => l.produitId === p.id)) {
      setErreur('Ce produit est déjà dans la commande');
      return;
    }
    const prix = Number(p.prixAchat) || 0;
    setLignes([...lignes, {
      produitId: p.id,
      produitNom: p.nom,
      produitCode: p.reference || p.codeBarres || '',
      prixUnitaire: prix,
      quantite: 1,
      sousTotal: prix,
    }]);
    setProduitAjout('');
    setErreur('');
  };

  const majLigne = (produitId: number, champ: 'prixUnitaire' | 'quantite', valeur: number) => {
    setLignes(lignes.map(l => {
      if (l.produitId !== produitId) return l;
      const maj = { ...l, [champ]: valeur };
      maj.sousTotal = (Number(maj.prixUnitaire) || 0) * (Number(maj.quantite) || 0);
      return maj;
    }));
  };

  const retirerLigne = (produitId: number) => {
    setLignes(lignes.filter(l => l.produitId !== produitId));
  };

  const totalHT = lignes.reduce((s, l) => s + l.sousTotal, 0);
  const totalTVA = tvaActive ? totalHT * 0.18 : 0;
  const totalTTC = totalHT + totalTVA;

  const enregistrer = async (envoyer: boolean) => {
    if (!fournisseurId) {
      setErreur('Veuillez sélectionner un fournisseur');
      return;
    }
    if (lignes.length === 0) {
      setErreur('Ajoutez au moins un produit');
      return;
    }
    const four = fournisseurs.find(f => f.id === fournisseurId);
    try {
      await api.post('/commandes', {
        fournisseurId,
        fournisseurNom: four?.nom,
        tvaActive,
        totalHT,
        totalTVA,
        totalTTC,
        statut: envoyer ? 'envoyee' : 'brouillon',
        dateLivraisonPrevue: dateLivraison || undefined,
        notes,
        lignes: lignes.map(l => ({
          produitId: l.produitId,
          produitNom: l.produitNom,
          produitCode: l.produitCode,
          prixUnitaire: l.prixUnitaire,
          quantite: l.quantite,
          sousTotal: l.sousTotal,
        })),
      });
      setShowModal(false);
      notifier(envoyer ? 'Bon de commande créé et envoyé' : 'Brouillon enregistré');
      chargerTout();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const changerStatut = async (cmd: Commande, statut: string) => {
    try {
      await api.put(`/commandes/${cmd.id}/statut`, { statut });
      notifier('Statut mis à jour');
      chargerTout();
      setShowDetail(false);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur');
    }
  };

  const receptionner = async (cmd: Commande) => {
    try {
      await api.put(`/commandes/${cmd.id}/recevoir`);
      notifier('Commande réceptionnée — stock mis à jour');
      chargerTout();
      setShowDetail(false);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de la réception');
    }
  };

  const annuler = async (cmd: Commande) => {
    try {
      await api.put(`/commandes/${cmd.id}/annuler`);
      notifier('Commande annulée');
      chargerTout();
      setShowDetail(false);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur');
    }
  };

  const supprimer = async () => {
    if (!commandeSel) return;
    try {
      await api.delete(`/commandes/${commandeSel.id}`);
      notifier('Commande supprimée');
      setShowDelete(false);
      setShowDetail(false);
      chargerTout();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const imprimer = () => window.print();

  if (chargement) return <div className="page-chargement">Chargement des commandes...</div>;

  return (
    <div className="commandes-page">
      <div className="page-header no-print">
        <div>
          <h1>Bons de commande fournisseur</h1>
          <p>Créez et suivez vos commandes d'approvisionnement</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouveau bon de commande
        </button>
      </div>

      {succes && <div className="alert-succes no-print">✅ {succes}</div>}
      {erreur && !showModal && <div className="alert-erreur no-print">⚠️ {erreur}</div>}

      {stats && (
        <div className="stats-bar no-print">
          <div className="stat-item">
            <span className="stat-val">{stats.total}</span>
            <span className="stat-lbl">Total commandes</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#854F0B' }}>{stats.enCours}</span>
            <span className="stat-lbl">En cours</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#3B6D11' }}>{stats.recues}</span>
            <span className="stat-lbl">Réceptionnées</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ fontSize: 16, color: '#185FA5' }}>
              {fmt(stats.montantEnCours)} F
            </span>
            <span className="stat-lbl">Montant en cours</span>
          </div>
        </div>
      )}

      <div className="commandes-table no-print">
        <table>
          <thead>
            <tr>
              <th>N° Commande</th>
              <th>Fournisseur</th>
              <th>Date</th>
              <th>Livraison prévue</th>
              <th>Articles</th>
              <th>Montant TTC</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {commandes.map(c => (
              <tr key={c.id} className="tr-clickable" onClick={() => { setCommandeSel(c); setShowDetail(true); }}>
                <td className="c-num">{c.numero}</td>
                <td>{c.fournisseurNom || '—'}</td>
                <td>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>{c.dateLivraisonPrevue ? new Date(c.dateLivraisonPrevue).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="text-center">{c.lignes?.length || 0}</td>
                <td className="c-montant">{fmt(c.totalTTC)} FCFA</td>
                <td>
                  <span className={`statut-badge ${STATUTS[c.statut]?.classe}`}>
                    {STATUTS[c.statut]?.label || c.statut}
                  </span>
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="td-actions">
                    {(c.statut === 'envoyee' || c.statut === 'partielle') && (
                      <button title="Réceptionner" onClick={() => receptionner(c)}>📥</button>
                    )}
                    {(c.statut === 'brouillon' || c.statut === 'envoyee') && (
                      <button title="Supprimer" onClick={() => { setCommandeSel(c); setShowDelete(true); }}>🗑️</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {commandes.length === 0 && (
          <div className="table-empty">Aucun bon de commande enregistré</div>
        )}
      </div>

      {/* MODAL CREATION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau bon de commande</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-row-2">
                <div className="form-group">
                  <label>Fournisseur <span className="req">*</span></label>
                  <select value={fournisseurId} onChange={e => setFournisseurId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">— Choisir un fournisseur —</option>
                    {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Livraison prévue</label>
                  <input type="date" value={dateLivraison} onChange={e => setDateLivraison(e.target.value)} />
                </div>
              </div>

              <div className="form-section">Produits à commander</div>
              <div className="ligne-ajout">
                <select value={produitAjout} onChange={e => setProduitAjout(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— Sélectionner un produit —</option>
                  {produits.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.nom} (stock: {p.stockActuel})
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-primary" onClick={ajouterLigne}>+ Ajouter</button>
              </div>

              {lignes.length > 0 && (
                <table className="lignes-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Prix achat</th>
                      <th>Qté</th>
                      <th>Sous-total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map(l => (
                      <tr key={l.produitId}>
                        <td>{l.produitNom}</td>
                        <td>
                          <input type="number" min={0} className="input-mini" value={l.prixUnitaire}
                            onChange={e => majLigne(l.produitId, 'prixUnitaire', Number(e.target.value))} />
                        </td>
                        <td>
                          <input type="number" min={1} className="input-mini" value={l.quantite}
                            onChange={e => majLigne(l.produitId, 'quantite', Number(e.target.value))} />
                        </td>
                        <td className="c-montant">{fmt(l.sousTotal)} F</td>
                        <td>
                          <button type="button" className="btn-retirer" onClick={() => retirerLigne(l.produitId)}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={tvaActive} onChange={e => setTvaActive(e.target.checked)} style={{ width: 'auto' }} />
                    Appliquer la TVA (18%)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="Conditions, instructions de livraison..."
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 7, fontFamily: 'inherit', fontSize: 13 }} />
              </div>

              <div className="recap-totaux">
                <div className="recap-row"><span>Total HT</span><span>{fmt(totalHT)} FCFA</span></div>
                {tvaActive && <div className="recap-row"><span>TVA 18%</span><span>{fmt(totalTVA)} FCFA</span></div>}
                <div className="recap-row total"><span>TOTAL TTC</span><span>{fmt(totalTTC)} FCFA</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-secondary" onClick={() => enregistrer(false)}>💾 Brouillon</button>
              <button className="btn-primary" onClick={() => enregistrer(true)}>📤 Créer et envoyer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETAIL + IMPRESSION */}
      {showDetail && commandeSel && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h2>Bon de commande — {commandeSel.numero}</h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* DOCUMENT IMPRIMABLE */}
              <div className="bc-document zone-impression">
                <div className="bc-entete">
                  <div>
                    {localStorage.getItem('nexacom_logo') ? (
                      <img src={localStorage.getItem('nexacom_logo')!} alt="Logo" className="bc-logo-img" />
                    ) : (
                      <div className="bc-logo">{configEntreprise.nom}</div>
                    )}
                    <div className="bc-coords">
                      {configEntreprise.adresse}<br />
                      Tél : {configEntreprise.telephone} — {configEntreprise.email}<br />
                      IFU : {configEntreprise.ifu}
                    </div>
                  </div>
                  <div className="bc-titre-zone">
                    <div className="bc-titre">BON DE COMMANDE</div>
                    <div className="bc-numero">N° {commandeSel.numero}</div>
                    <div className="bc-date">Date : {new Date(commandeSel.createdAt).toLocaleDateString('fr-FR')}</div>
                  </div>
                </div>

                <div className="bc-fournisseur">
                  <div className="bc-bloc-titre">FOURNISSEUR</div>
                  <div className="bc-four-nom">{commandeSel.fournisseurNom}</div>
                  {commandeSel.dateLivraisonPrevue && (
                    <div className="bc-livraison">
                      Livraison souhaitée : {new Date(commandeSel.dateLivraisonPrevue).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </div>

                <table className="bc-table">
                  <thead>
                    <tr>
                      <th>Réf.</th>
                      <th>Désignation</th>
                      <th className="text-right">Prix unitaire</th>
                      <th className="text-center">Qté</th>
                      <th className="text-right">Sous-total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commandeSel.lignes?.map((l, i) => (
                      <tr key={i}>
                        <td>{l.produitCode || '—'}</td>
                        <td>{l.produitNom}</td>
                        <td className="text-right">{fmt(l.prixUnitaire)} F</td>
                        <td className="text-center">{l.quantite}</td>
                        <td className="text-right">{fmt(l.sousTotal)} F</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="bc-totaux">
                  <div className="bc-tot-row"><span>Total HT</span><span>{fmt(commandeSel.totalHT)} FCFA</span></div>
                  {commandeSel.tvaActive && (
                    <div className="bc-tot-row"><span>TVA 18%</span><span>{fmt(commandeSel.totalTVA)} FCFA</span></div>
                  )}
                  <div className="bc-tot-row total"><span>TOTAL TTC</span><span>{fmt(commandeSel.totalTTC)} FCFA</span></div>
                </div>

                {commandeSel.notes && (
                  <div className="bc-notes">
                    <strong>Notes :</strong> {commandeSel.notes}
                  </div>
                )}

                <div className="bc-signatures">
                  <div className="bc-sig">
                    <div className="bc-sig-titre">Le Responsable des achats</div>
                    <div className="bc-sig-ligne"></div>
                  </div>
                  <div className="bc-sig">
                    <div className="bc-sig-titre">Bon pour accord (Fournisseur)</div>
                    <div className="bc-sig-ligne"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer no-print">
              {commandeSel.statut !== 'recue' && commandeSel.statut !== 'annulee' && (
                <button className="btn-danger-outline" onClick={() => annuler(commandeSel)}>Annuler la commande</button>
              )}
              {commandeSel.statut === 'brouillon' && (
                <button className="btn-secondary" onClick={() => changerStatut(commandeSel, 'envoyee')}>📤 Marquer envoyée</button>
              )}
              {(commandeSel.statut === 'envoyee' || commandeSel.statut === 'partielle') && (
                <button className="btn-success" onClick={() => receptionner(commandeSel)}>📥 Réceptionner (entrée stock)</button>
              )}
              <button className="btn-secondary" onClick={imprimer}>🖨️ Imprimer</button>
              <button className="btn-secondary" onClick={() => setShowDetail(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDelete && commandeSel && (
        <div className="modal-overlay" onClick={() => setShowDelete(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer la commande</h2>
              <button className="modal-close" onClick={() => setShowDelete(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer le bon de commande <strong>{commandeSel.numero}</strong> ? Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDelete(false)}>Annuler</button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
